import { useEffect, useLayoutEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent, type PointerEvent } from 'react';
import { IconClose, IconMic, IconPaperclip, IconReply, IconSend, IconTrash } from '../components/Icons';
import { formatSize } from '../lib/format';
import { canRecordVoice, startRecording, type Recording } from '../lib/recorder';
import { sendFiles, sendText, sendVoice, setReplyDraft, useChat } from '../store/chat';
import { replyPreview } from './MessageBubble';
import { isPermissionDenied, showMediaHelp } from '../lib/mediaHelp';

// Client-side pre-check for fast feedback; the server enforces the real limits.
const LIMITS = { image: 20 * 1024 * 1024, video: 100 * 1024 * 1024, file: 20 * 1024 * 1024 };
const limitFor = (f: File) => (f.type.startsWith('video/') ? LIMITS.video : f.type.startsWith('image/') ? LIMITS.image : LIMITS.file);

const MAX_VOICE_MS = 10 * 60 * 1000;
const MIN_VOICE_MS = 700;
// Hold-to-record gestures (px): slide left to cancel, slide up to lock.
const CANCEL_DX = 110;
const LOCK_DY = 80;
const HOLD_HINT = 'Удерживайте микрофон, чтобы записать голосовое';
const MIC_DENIED = 'Нет доступа к микрофону. Разрешите его в настройках браузера или устройства.';
const MIC_UNAVAILABLE = 'Микрофон недоступен — возможно, его занимает другое приложение.';

/** 0:02,62 */
const formatRecTime = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')},${String(Math.floor((ms % 1000) / 10)).padStart(2, '0')}`;
};

const IconLock = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);
const IconChevronUp = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 15l6-6 6 6" />
  </svg>
);

type RecMode = 'idle' | 'holding' | 'locked';

const draftKey = (id: string) => `evo:draft:${id}`;
const readDraft = (id: string) => {
  try {
    return localStorage.getItem(draftKey(id)) ?? '';
  } catch {
    return '';
  }
};
const writeDraft = (id: string, v: string) => {
  try {
    if (v) localStorage.setItem(draftKey(id), v);
    else localStorage.removeItem(draftKey(id));
  } catch {
    /* storage unavailable */
  }
};

/**
 * Telegram-style composer floating over the chat: round attach button, rounded input, and one action
 * button on the right — "send" when there is text, a microphone when the field is empty.
 *
 * Voice, as in Telegram: press and hold the microphone to record (it grows and pulses with the voice,
 * a timer runs), release to send, slide left to cancel, slide up to lock — then [delete] [timer] [send].
 */
export function Composer({ conversationId, onHeight }: { conversationId: string; onHeight: (h: number) => void }) {
  const [text, setText] = useState(() => readDraft(conversationId));
  const [notice, setNotice] = useState<string | null>(null);
  const [recording, setRecording] = useState<Recording | null>(null);
  const [mode, setMode] = useState<RecMode>('idle');
  const [drag, setDrag] = useState({ dx: 0, dy: 0 });
  const [elapsed, setElapsed] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const micRef = useRef<HTMLButtonElement>(null);
  const recordingRef = useRef<Recording | null>(null);
  recordingRef.current = recording;
  // The press that started the current recording.
  const gesture = useRef<{ id: number; x: number; y: number; released: boolean; cancelled: boolean } | null>(null);
  const reply = useChat((s) => s.replyDraft[conversationId]);
  const replyName = useChat((s) =>
    reply ? (reply.senderId === s.me?.id ? 'Вы' : (s.conversations[conversationId]?.peer.displayName ?? '')) : '',
  );

  // Choosing "Reply" puts the cursor in the field, like Telegram.
  useEffect(() => {
    if (reply) ref.current?.focus({ preventScroll: true });
  }, [reply]);

  useEffect(() => writeDraft(conversationId, text), [conversationId, text]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [text, mode]);

  // The message list reserves space under its last message for this floating panel.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => onHeight(el.offsetHeight));
    ro.observe(el);
    onHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, [onHeight]);

  // Recording timer and auto-send at the length limit.
  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => {
      const ms = Date.now() - recording.startedAt;
      setElapsed(ms);
      if (ms >= MAX_VOICE_MS) void finishRecording();
    }, 50);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);

  // Pulsing halo: the microphone's glow follows the voice loudness (written to a CSS variable, no re-render).
  useEffect(() => {
    if (mode !== 'holding' || !recording) return;
    let raf = 0;
    const tick = () => {
      micRef.current?.style.setProperty('--level', recording.level().toFixed(3));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [mode, recording]);

  // Closing the chat mid-recording discards it.
  useEffect(() => () => recordingRef.current?.cancel(), []);

  const submit = () => {
    if (!text.trim()) return;
    void sendText(conversationId, text);
    setText('');
    ref.current?.focus();
  };

  const attach = (files: File[]) => {
    const tooBig = files.filter((f) => f.size > limitFor(f));
    const ok = files.filter((f) => f.size <= limitFor(f) && f.size > 0);
    setNotice(tooBig.length ? `Слишком большой файл: ${tooBig.map((f) => `${f.name} (${formatSize(f.size)})`).join(', ')}` : null);
    if (ok.length) void sendFiles(conversationId, ok);
  };

  const resetRecording = () => {
    setRecording(null);
    setMode('idle');
    setDrag({ dx: 0, dy: 0 });
    gesture.current = null;
  };

  const finishRecording = async () => {
    const r = recordingRef.current;
    resetRecording();
    if (!r) return;
    try {
      const clip = await r.stop();
      if (clip.durationMs < MIN_VOICE_MS || clip.blob.size === 0) return setNotice(HOLD_HINT);
      void sendVoice(conversationId, clip.blob, clip.durationMs, clip.extension);
    } catch {
      setNotice('Не удалось записать голосовое сообщение');
    }
  };

  const cancelRecording = () => {
    recordingRef.current?.cancel();
    resetRecording();
    navigator.vibrate?.(15);
  };

  // ── Hold-to-record gesture on the microphone ─────────────────────────────────
  const onMicDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (mode !== 'idle' || e.button > 0) return;
    e.preventDefault(); // no focus change, text selection or long-press menu
    if (!canRecordVoice()) return setNotice('Запись голоса не поддерживается этим браузером');
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const g = { id: e.pointerId, x: e.clientX, y: e.clientY, released: false, cancelled: false };
    gesture.current = g;
    setNotice(null);
    setDrag({ dx: 0, dy: 0 });
    setElapsed(0);
    setMode('holding');
    startRecording().then(
      (r) => {
        // Released or cancelled before the microphone was ready (e.g. the permission prompt took the touch).
        if (g.released || g.cancelled || gesture.current !== g) {
          r.cancel();
          if (gesture.current === g || gesture.current === null) resetRecording();
          if (g.released && !g.cancelled) setNotice(HOLD_HINT);
          return;
        }
        setRecording(r);
        navigator.vibrate?.(20);
      },
      (err) => {
        resetRecording();
        if (isPermissionDenied(err)) {
          setNotice(MIC_DENIED);
          showMediaHelp();
        } else {
          setNotice(MIC_UNAVAILABLE);
        }
      },
    );
  };

  const onMicMove = (e: PointerEvent<HTMLButtonElement>) => {
    const g = gesture.current;
    if (!g || e.pointerId !== g.id || mode !== 'holding') return;
    const dx = Math.min(0, e.clientX - g.x);
    const dy = Math.min(0, e.clientY - g.y);
    if (dx < -CANCEL_DX) {
      g.cancelled = true;
      cancelRecording();
    } else if (dy < -LOCK_DY && recordingRef.current) {
      gesture.current = null;
      setDrag({ dx: 0, dy: 0 });
      setMode('locked');
      navigator.vibrate?.(20);
    } else {
      setDrag({ dx, dy });
    }
  };

  const onMicUp = (e: PointerEvent<HTMLButtonElement>) => {
    const g = gesture.current;
    if (!g || e.pointerId !== g.id) return;
    g.released = true;
    // Not recording yet: the start handler sees `released` and shows the hint instead.
    if (mode === 'holding' && recordingRef.current) void finishRecording();
  };

  const onMicCancel = (e: PointerEvent<HTMLButtonElement>) => {
    const g = gesture.current;
    if (!g || e.pointerId !== g.id) return;
    g.cancelled = true;
    if (recordingRef.current) cancelRecording();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends on devices with a physical keyboard; on touch devices Enter inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && !matchMedia('(pointer: coarse)').matches) {
      e.preventDefault();
      submit();
    }
  };

  const onPaste = (e: ClipboardEvent) => {
    const files = [...e.clipboardData.files];
    if (files.length) {
      e.preventDefault();
      attach(files);
    }
  };

  const hasText = text.trim().length > 0;
  const holding = mode === 'holding';
  const cancelProgress = Math.min(1, -drag.dx / CANCEL_DX);
  const lockProgress = Math.min(1, -drag.dy / LOCK_DY);

  return (
    <div className="composer-wrap" ref={wrapRef}>
      {notice && (
        <div className="composer-notice glass">
          <span>{notice}</span>
          <button className="icon-btn small" onClick={() => setNotice(null)} aria-label="Закрыть">
            <IconClose size={14} />
          </button>
        </div>
      )}
      {reply && mode === 'idle' && (
        <div className="composer-reply glass">
          <IconReply size={20} />
          <span className="composer-reply-body">
            <span className="reply-name">{replyName}</span>
            <span className="reply-text">{replyPreview(reply)}</span>
          </span>
          <button
            className="icon-btn small"
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => setReplyDraft(conversationId, null)}
            aria-label="Отменить ответ"
          >
            <IconClose size={16} />
          </button>
        </div>
      )}
      <div className="composer">
        {mode === 'locked' ? (
          <button className="round-btn glass danger-icon" onClick={cancelRecording} aria-label="Удалить запись">
            <IconTrash size={21} />
          </button>
        ) : mode === 'idle' ? (
          <button className="round-btn glass" onClick={() => fileRef.current?.click()} aria-label="Прикрепить файл">
            <IconPaperclip size={22} />
          </button>
        ) : null}
        <input
          ref={fileRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            attach([...(e.target.files ?? [])]);
            e.target.value = '';
          }}
        />

        {mode === 'idle' ? (
          <div className="input-pill glass">
            <textarea
              ref={ref}
              rows={1}
              placeholder="Сообщение"
              value={text}
              maxLength={4000}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              enterKeyHint="send"
            />
          </div>
        ) : (
          <div className={`input-pill glass recording${holding ? ' holding' : ''}`} role="status">
            <span className="rec-dot" />
            <span className="rec-time">{formatRecTime(elapsed)}</span>
            {holding ? (
              <span className="rec-hint" style={{ transform: `translateX(${drag.dx * 0.6}px)`, opacity: 1 - cancelProgress * 0.9 }}>
                ‹ Влево — отмена
              </span>
            ) : (
              <span className="rec-label">Запись…</span>
            )}
          </div>
        )}

        {mode === 'locked' || (mode === 'idle' && hasText) ? (
          <button
            className="round-btn send"
            // Keep focus in the field so the keyboard stays open after sending (iOS).
            onPointerDown={(e) => mode === 'idle' && e.preventDefault()}
            onClick={mode === 'locked' ? () => void finishRecording() : submit}
            aria-label="Отправить"
          >
            <IconSend size={20} />
          </button>
        ) : (
          <div className="mic-slot">
            {holding && (
              <div
                className="rec-lock glass"
                style={{ transform: `translateY(${drag.dy * 0.5}px)` }}
                aria-hidden="true"
              >
                <IconLock />
                <span style={{ opacity: 1 - lockProgress }}>
                  <IconChevronUp />
                </span>
              </div>
            )}
            <button
              ref={micRef}
              className={`round-btn mic${holding ? ' holding' : ' glass'}`}
              style={holding ? { transform: `translateX(${drag.dx}px)` } : undefined}
              onPointerDown={onMicDown}
              onPointerMove={onMicMove}
              onPointerUp={onMicUp}
              onPointerCancel={onMicCancel}
              onContextMenu={(e) => e.preventDefault()}
              aria-label="Записать голосовое сообщение (удерживайте)"
            >
              <IconMic size={holding ? 30 : 22} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
